import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clinicService } from '../../services/api';

// Import icons
import { ReactComponent as UploadIcon } from '../../assets/upload.svg';

const ConfigImportSection = ({ onImportSuccess = null }) => {
  const navigate = useNavigate();
  const [isImporting, setIsImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [newClinicId, setNewClinicId] = useState('');
  const [newClinicName, setNewClinicName] = useState('');
  const [importData, setImportData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      setImportError(null);
      setShowPreview(false);

      // Read the file content
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          setImportData(data);

          // Pre-fill fields with values from the import data
          if (data.clinic_configuration && data.clinic_configuration.id) {
            setNewClinicId(data.clinic_configuration.id + '_copy');
          }
          if (data.clinic_configuration && data.clinic_configuration.name) {
            setNewClinicName(data.clinic_configuration.name + ' (Copy)');
          }
        } catch (error) {
          setImportError('Invalid JSON file. Please upload a valid clinic configuration file.');
          setImportData(null);
        }
      };
      reader.readAsText(file);
    }
  };

  const validateImport = () => {
    if (!importData) {
      setImportError('No import data available. Please upload a file first.');
      return false;
    }

    if (!newClinicId) {
      setImportError('New clinic ID is required.');
      return false;
    }

    if (!newClinicName) {
      setImportError('New clinic name is required.');
      return false;
    }

    return true;
  };

  const handleShowPreview = () => {
    if (validateImport()) {
      setShowPreview(true);
    }
  };

  const handleImport = async () => {
    if (!validateImport()) {
      return;
    }

    try {
      setIsImporting(true);
      setImportError(null);
      setImportSuccess(null);

      // Prepare the import data with new clinic ID and name
      const dataToImport = {
        ...importData,
        targetClinicId: newClinicId,
        targetClinicName: newClinicName
      };

      const response = await clinicService.importClinicConfiguration(dataToImport);

      const successMessage = `Clinic configuration imported successfully. New clinic ID: ${response.clinic_id}`;
      setImportSuccess(successMessage);

      // Reset form
      setImportFile(null);
      setImportData(null);
      setNewClinicId('');
      setNewClinicName('');
      setShowPreview(false);

      // Call the onImportSuccess callback if provided
      if (onImportSuccess) {
        setTimeout(() => {
          onImportSuccess(response.clinic_id);
        }, 1500);
      } else {
        // Navigate to the new clinic after a delay
        setTimeout(() => {
          navigate(`/clinics/${response.clinic_id}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Error importing clinic configuration:', error);
      setImportError('Failed to import clinic configuration. Please check the file and try again.');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="bg-gray-50 p-6 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-700 mb-6">Clinic Configuration Import</h3>

      <div className="bg-white p-6 rounded-lg shadow">
        <h4 className="text-md font-semibold mb-4 flex items-center">
          <UploadIcon className="h-5 w-5 mr-2 text-gray-600" />
          Import Configuration
        </h4>
        <p className="text-gray-600 mb-4">
          Import a clinic configuration to create a new clinic. You can customize the new clinic's
          ID and name.
        </p>

        {importError && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {importError}
          </div>
        )}

        {importSuccess && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
            {importSuccess}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configuration File
            </label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileChange}
              className="w-full p-2 border rounded"
              disabled={isImporting}
            />
            <p className="mt-1 text-xs text-gray-500">
              Select a JSON file containing clinic configuration data
            </p>
          </div>

          {importData && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Clinic ID
                </label>
                <input
                  type="text"
                  value={newClinicId}
                  onChange={(e) => setNewClinicId(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter new clinic ID"
                  disabled={isImporting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  This ID will be used for the new clinic
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Clinic Name
                </label>
                <input
                  type="text"
                  value={newClinicName}
                  onChange={(e) => setNewClinicName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter new clinic name"
                  disabled={isImporting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  This name will be displayed for the new clinic
                </p>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {!showPreview ? (
                  <button
                    onClick={handleShowPreview}
                    disabled={isImporting}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
                  >
                    Preview Import Data
                  </button>
                ) : (
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Hide Preview
                  </button>
                )}

                <button
                  onClick={handleImport}
                  disabled={isImporting}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-green-300 flex items-center"
                >
                  <UploadIcon className="h-4 w-4 mr-2" />
                  {isImporting ? 'Importing...' : 'Import as New Clinic'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Import Preview */}
      {showPreview && importData && (
        <div className="mt-8 bg-white p-6 rounded-lg shadow">
          <h4 className="text-md font-semibold mb-4">Import Data Preview</h4>
          <div className="overflow-auto max-h-96 bg-gray-50 p-4 rounded border">
            <pre className="text-xs font-mono">
              {JSON.stringify(importData, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigImportSection;
